/**
 * Discount Calculator Utility
 * ===========================
 * Calculates various discount percentages for subscription plans.
 * All calculations are done on the frontend for consistency and flexibility.
 */

export interface DiscountCalculationResult {
  /** Promotion discount percentage (base_price vs discounted_price) */
  promotionDiscount: number | null;
  /** Annual savings percentage (annual discounted vs 12x monthly discounted) */
  annualSavings: number | null;
  /** Combined discount for annual plans (promotion + annual savings) */
  combinedDiscount: number | null;
  /** Whether there's any discount applied */
  hasDiscount: boolean;
}

/**
 * Calculate promotion discount percentage.
 * 
 * @param basePrice - Base price before discount
 * @param discountedPrice - Discounted price
 * @returns Discount percentage (0-100) or null if no discount
 */
export function calculatePromotionDiscount(
  basePrice: number | null | undefined,
  discountedPrice: number | null | undefined
): number | null {
  if (!basePrice || !discountedPrice || basePrice <= 0) {
    return null;
  }

  if (discountedPrice >= basePrice) {
    return null; // No discount
  }

  const discountAmount = basePrice - discountedPrice;
  const discountPercentage = (discountAmount / basePrice) * 100;
  
  return Math.round(discountPercentage * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate annual savings percentage.
 * Compares annual discounted price vs 12x monthly discounted price.
 * 
 * @param annualDiscountedPrice - Discounted annual price
 * @param monthlyDiscountedPrice - Discounted monthly price
 * @returns Savings percentage (0-100) or null if no savings
 */
export function calculateAnnualSavings(
  annualDiscountedPrice: number | null | undefined,
  monthlyDiscountedPrice: number | null | undefined
): number | null {
  if (!annualDiscountedPrice || !monthlyDiscountedPrice || monthlyDiscountedPrice <= 0) {
    return null;
  }

  const twelveXMonthly = monthlyDiscountedPrice * 12;
  
  if (annualDiscountedPrice >= twelveXMonthly) {
    return null; // No savings
  }

  const savingsAmount = twelveXMonthly - annualDiscountedPrice;
  const savingsPercentage = (savingsAmount / twelveXMonthly) * 100;
  
  return Math.round(savingsPercentage * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate combined discount for annual plans.
 * This represents the total savings when both promotion and annual savings apply.
 * 
 * Formula: Combined = 1 - (annual_discounted / (monthly_base * 12))
 * This shows total savings vs paying monthly at base price.
 * 
 * @param annualDiscountedPrice - Discounted annual price
 * @param monthlyBasePrice - Base monthly price (before promotion discount)
 * @returns Combined discount percentage (0-100) or null
 */
export function calculateCombinedDiscount(
  annualDiscountedPrice: number | null | undefined,
  monthlyBasePrice: number | null | undefined
): number | null {
  if (!annualDiscountedPrice || !monthlyBasePrice || monthlyBasePrice <= 0) {
    return null;
  }

  const twelveXMonthlyBase = monthlyBasePrice * 12;
  
  if (annualDiscountedPrice >= twelveXMonthlyBase) {
    return null; // No discount
  }

  const discountAmount = twelveXMonthlyBase - annualDiscountedPrice;
  const discountPercentage = (discountAmount / twelveXMonthlyBase) * 100;
  
  return Math.round(discountPercentage * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate all discount metrics for a subscription plan.
 * 
 * @param plan - Plan object with pricing information
 * @param monthlyPlan - Corresponding monthly plan (required for annual plans)
 * @returns Complete discount calculation results
 */
export function calculateAllDiscounts(
  plan: {
    base_price: number | null | undefined;
    discounted_price: number | null | undefined;
    billing_cycle: string;
  },
  monthlyPlan?: {
    base_price: number | null | undefined;
    discounted_price: number | null | undefined;
  } | null
): DiscountCalculationResult {
  const promotionDiscount = calculatePromotionDiscount(
    plan.base_price,
    plan.discounted_price
  );

  let annualSavings: number | null = null;
  let combinedDiscount: number | null = null;

  // For annual plans, calculate annual savings and combined discount
  if (plan.billing_cycle === 'annual' && monthlyPlan) {
    // Annual savings: compare annual discounted vs 12x monthly discounted
    annualSavings = calculateAnnualSavings(
      plan.discounted_price,
      monthlyPlan.discounted_price
    );

    // Combined discount: compare annual discounted vs 12x monthly base
    // This shows total savings including both promotion and annual discount
    combinedDiscount = calculateCombinedDiscount(
      plan.discounted_price,
      monthlyPlan.base_price
    );
  }

  const hasDiscount = promotionDiscount !== null || annualSavings !== null || combinedDiscount !== null;

  return {
    promotionDiscount,
    annualSavings,
    combinedDiscount,
    hasDiscount,
  };
}

/**
 * Format discount percentage for display.
 * 
 * @param percentage - Discount percentage (0-100)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "25.50%")
 */
export function formatDiscountPercentage(
  percentage: number | null,
  decimals: number = 2
): string {
  if (percentage === null || percentage === undefined) {
    return '';
  }

  return `${percentage.toFixed(decimals)}%`;
}

